/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import type { PlotlyGroupKey, PlotlyGroupOption } from '@/config/eeCheckConfig';

/* Import components */
import PlotlyFigure from '@/components/charts/PlotlyFigure';

/* Import types */
import type { StoredPlotlyResponse } from '@/types/eeCheck';
import type { StoredPlotlyRequest } from '@/types/eeCheck';
import type { PlotlySubsensor } from '@/types/ui';

interface PlotlyTabProps {
    activePlotlyGroupLabel: string
    activePlotlyReferences: string[]
    activePlotlySubsensorOptions: string[]
    activePlotlyRangeOptions: string[]
    hasSession: boolean
    isRequestingData: boolean
    meters: string[]
    onRequestExample: () => void
    onSelectedGroupChange: (group: PlotlyGroupKey) => void
    onSelectedMeterChange: (meter: string) => void
    onSelectedRangeChange: (range: string) => void
    onSelectedReferenceChange: (reference: string) => void
    onSelectedSubsensorChange: (subsensor: PlotlySubsensor) => void
    outputMode: 'empty' | 'plotly' | 'raw' | 'timeseries'
    plotlyFigure: StoredPlotlyResponse | null
    plotlyGroupOptions: PlotlyGroupOption[]
    plotlyRequest: StoredPlotlyRequest
    plotlyRequestMeter: string
    selectedGroup: PlotlyGroupKey
    selectedMeter: string
    selectedRange: string
    selectedReference: string
    selectedSensorDescription: string
    selectedSubsensor: PlotlySubsensor
}

function PlotlyTab({
    activePlotlyGroupLabel,
    activePlotlyRangeOptions,
    activePlotlyReferences,
    activePlotlySubsensorOptions,
    hasSession,
    isRequestingData,
    meters,
    onRequestExample,
    onSelectedGroupChange,
    onSelectedMeterChange,
    onSelectedRangeChange,
    onSelectedReferenceChange,
    onSelectedSubsensorChange,
    outputMode,
    plotlyFigure,
    plotlyGroupOptions,
    plotlyRequest,
    plotlyRequestMeter,
    selectedGroup,
    selectedMeter,
    selectedRange,
    selectedReference,
    selectedSensorDescription,
    selectedSubsensor,
}: PlotlyTabProps) {
    const { locale, t } = useI18n();
    const sortedPlotlyGroupOptions = [...plotlyGroupOptions].sort((left, right) =>
        left.label.localeCompare(right.label, locale),
    );
    const sortedMeters = [...meters].sort((left, right) =>
        left.localeCompare(right, locale),
    );
    const sortedPlotlyReferences = [...activePlotlyReferences].sort((left, right) =>
        left.localeCompare(right, locale),
    );
    const sortedPlotlySubsensorOptions = [...activePlotlySubsensorOptions].sort(
        (left, right) => left.localeCompare(right, locale),
    );

    return (
        <div className="flex flex-col gap-6">
            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <div className="grid w-full grid-cols-1 gap-y-4 text-left xl:grid-cols-5 xl:divide-x xl:divide-base-content">
                    <div className="flex min-h-12 items-center gap-3 xl:pr-5">
                        <label className="text-sm font-semibold" htmlFor="plotly-group-select">
                            {t('Group')}
                        </label>
                        <select
                            id="plotly-group-select"
                            className="select select-bordered w-auto"
                            value={selectedGroup}
                            onChange={(event) =>
                                onSelectedGroupChange(event.target.value as PlotlyGroupKey)
                            }
                        >
                            {sortedPlotlyGroupOptions.map((group) => (
                                <option key={group.value} value={group.value}>
                                    {group.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex min-h-12 items-center gap-3 xl:px-5">
                        <label className="text-sm font-semibold" htmlFor="plotly-meter-select">
                            {t('Meter')}
                        </label>
                        <select
                            id="plotly-meter-select"
                            className="select select-bordered w-auto"
                            value={selectedMeter}
                            onChange={(event) => onSelectedMeterChange(event.target.value)}
                        >
                            {sortedMeters.map((meter) => (
                                <option key={meter} value={meter}>
                                    {meter}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex min-h-12 items-center gap-3 xl:px-5">
                        <label
                            className="text-sm font-semibold"
                            htmlFor="plotly-reference-select"
                        >
                            {t('Reference')}
                        </label>
                        <select
                            id="plotly-reference-select"
                            className="select select-bordered w-auto"
                            value={selectedReference}
                            onChange={(event) => onSelectedReferenceChange(event.target.value)}
                        >
                            {sortedPlotlyReferences.map((reference) => (
                                <option key={reference} value={reference}>
                                    {reference}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex min-h-12 items-center gap-3 xl:px-5">
                        <label
                            className="text-sm font-semibold"
                            htmlFor="plotly-subsensor-select"
                        >
                            {t('Subsensor')}
                        </label>
                        <select
                            id="plotly-subsensor-select"
                            className="select select-bordered w-auto"
                            value={selectedSubsensor}
                            onChange={(event) =>
                                onSelectedSubsensorChange(event.target.value as PlotlySubsensor)
                            }
                            disabled={activePlotlySubsensorOptions.length === 0}
                        >
                            {activePlotlySubsensorOptions.length === 0 ? (
                                <option value="">{t('No subsensor')}</option>
                            ) : null}
                            {sortedPlotlySubsensorOptions.map((subsensor) => (
                                <option key={subsensor} value={subsensor}>
                                    {subsensor}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex min-h-12 items-center gap-3 xl:pl-5">
                        <label className="text-sm font-semibold" htmlFor="plotly-range-select">
                            {t('Range')}
                        </label>
                        {activePlotlyRangeOptions.length > 0 ? (
                            <select
                                id="plotly-range-select"
                                className="select select-bordered w-auto"
                                value={selectedRange}
                                onChange={(event) => onSelectedRangeChange(event.target.value)}
                            >
                                {activePlotlyRangeOptions.map((range) => (
                                    <option key={range} value={range}>
                                        {range}
                                    </option>
                                ))}
                            </select>
                        ) : <span aria-label={t('Not available')}>-</span>}
                    </div>
                </div>
            </section>

            <section className="dashboard-panel flex items-stretch justify-start p-5">
                <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
                    <dl className="grid gap-3">
                        <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                            <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                {t('Selected group')}
                            </dt>
                            <dd className="mt-1 break-words text-sm text-base-content">{activePlotlyGroupLabel}</dd>
                        </div>

                        <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                            <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                {t('Selected sensor')}
                            </dt>
                            <dd className="mt-1 break-words text-sm text-base-content">{selectedSensorDescription}</dd>
                        </div>

                        <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                            <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                {t('Plotly request target')}
                            </dt>
                            <dd className="mt-1 break-words text-sm text-base-content">
                                {t('{meter} using {reference} with {range}', {
                                    meter: plotlyRequestMeter,
                                    reference: selectedReference,
                                    range: selectedRange,
                                })}
                            </dd>
                        </div>
                    </dl>

                    <div className="flex min-w-0 flex-col">
                        <h2 className="mb-2 text-sm font-semibold">{t('API request payload')}</h2>
                        <pre className="min-h-0 flex-1 overflow-auto rounded-box bg-base-200/60 p-4 text-left text-xs text-base-content/80"><code>{JSON.stringify(plotlyRequest, null, 4)}</code></pre>
                    </div>
                </div>
            </section>

            <div>
                <button
                    className="btn btn-primary"
                    type="button"
                    onClick={onRequestExample}
                    disabled={!hasSession || isRequestingData}
                >
                    {isRequestingData ? t('Requesting Plotly JSON...') : t('Request Plotly JSON')}
                </button>
            </div>

            {outputMode === 'plotly' && plotlyFigure ? (
                <div>
                    <label className="mb-2 block text-sm font-semibold">{t('Plotly chart output')}</label>
                    <PlotlyFigure figure={plotlyFigure} />
                </div>
            ) : (
                <section className="dashboard-panel flex items-stretch justify-start p-5">
                    <div className="w-full rounded-box bg-base-200/60 px-3 py-2 text-sm text-base-content/70">
                        {t('No Plotly chart loaded yet.')}
                    </div>
                </section>
            )}

        </div>
    );
}

export default PlotlyTab;
